import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Trash } from "@phosphor-icons/react";
import { BrutalButton } from "./Brutal";

/**
 * Shared cascade-confirmation dialog used by:
 *   - InventoryPage (when the user soft-deletes a product — no longer used here)
 *   - TrashPage (when the user permanently purges a soft-deleted product)
 *
 * Props:
 *   target = { product, impact: { product_name, child_count, consigned_units, movement_log_count } }
 *   onCancel()  → closes the dialog
 *   onConfirm() → user said "Delete everything"; parent runs the actual purge
 */
export default function DeleteCascadeDialog({ target, onCancel, onConfirm }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const impact = target?.impact || {};
  const productName = impact.product_name || target?.product?.name || "";

  const confirm = async () => {
    setBusy(true);
    try {
      await onConfirm();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onCancel}>
      <div
        className="bg-white border border-zinc-200 rounded-xl shadow-xl p-6 w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
        data-testid="delete-cascade-dialog"
      >
        <div className="flex items-start gap-3 mb-3">
          <div className="w-9 h-9 rounded-full bg-red-100 text-red-700 flex items-center justify-center shrink-0">
            <Trash size={18} weight="bold" />
          </div>
          <div>
            <h2 className="text-lg font-semibold tracking-tight">{t("inventory.cascade_title")}</h2>
            <p className="text-sm text-zinc-600 mt-0.5">{t("inventory.cascade_subtitle", { name: productName })}</p>
          </div>
        </div>

        <ul
          className="bg-zinc-50 border border-zinc-200 rounded-lg p-3 text-sm space-y-1 mb-4"
          data-testid="delete-cascade-impact"
        >
          {impact.child_count > 0 && (
            <li className="flex justify-between">
              <span className="text-zinc-600">{t("inventory.cascade_child_count")}</span>
              <span className="font-semibold tabular-nums">{impact.child_count}</span>
            </li>
          )}
          {impact.consigned_units > 0 && (
            <li className="flex justify-between">
              <span className="text-zinc-600">{t("inventory.cascade_consigned_units")}</span>
              <span className="font-semibold tabular-nums">{impact.consigned_units}</span>
            </li>
          )}
          {impact.movement_log_count > 0 && (
            <li className="flex justify-between">
              <span className="text-zinc-600">{t("inventory.cascade_movement_logs")}</span>
              <span className="font-semibold tabular-nums">{impact.movement_log_count}</span>
            </li>
          )}
        </ul>

        <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-md p-2 mb-4">
          {t("inventory.cascade_irreversible")}
        </p>

        <div className="flex gap-3">
          <BrutalButton type="button" variant="secondary" className="flex-1" onClick={onCancel} disabled={busy}>
            {t("common.cancel")}
          </BrutalButton>
          <BrutalButton
            type="button"
            variant="warning"
            className="flex-1"
            onClick={confirm}
            disabled={busy}
            data-testid="delete-cascade-confirm"
          >
            {busy ? t("common.deleting") : t("inventory.cascade_confirm")}
          </BrutalButton>
        </div>
      </div>
    </div>
  );
}
